import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface DraggableChatWrapperProps {
  children: React.ReactNode;
  isOpen: boolean;
}

const DraggableChatWrapper: React.FC<DraggableChatWrapperProps> = ({ children, isOpen }) => {
  // We only care about horizontal position (right offset)
  const [position, setPosition] = useState({ x: 16 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, initialX: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Reset position to initial (right 16px) when opened
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 16 });
    }
  }, [isOpen]);

  // Handle window resize to keep chatbot in bounds
  useEffect(() => {
    const handleResize = () => {
      if (!isOpen || !wrapperRef.current) return;
      
      const windowWidth = window.innerWidth;
      const chatWidth = wrapperRef.current.offsetWidth;
      
      const minX = 16;
      const maxX = windowWidth - chatWidth - 16;
      
      setPosition(prev => ({
        x: Math.max(minX, Math.min(prev.x, maxX))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only drag from the header or a specific handle if needed, but for now let's allow dragging from a designated area
    // or the whole top part. We'll use the top bar for dragging.
    const target = e.target as HTMLElement;
    if (target.closest('.drag-handle')) {
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        initialX: position.x,
      };
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = dragStartRef.current.x - e.clientX;
      let newX = dragStartRef.current.initialX + deltaX;

      // Constraints: keep within window
      const windowWidth = window.innerWidth;
      const chatWidth = wrapperRef.current?.offsetWidth || 400;
      
      const minX = 16; // Minimum 16px from right
      const maxX = windowWidth - chatWidth - 16; // Minimum 16px from left

      newX = Math.max(minX, Math.min(newX, maxX));

      setPosition({ x: newX });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  return (
    <Box
      ref={wrapperRef}
      onMouseDown={handleMouseDown}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: position.x,
        zIndex: 10000,
        width: { xs: 'calc(100vw - 32px)', sm: '400px' },
        height: '600px',
        maxHeight: 'calc(100vh - 100px)',
        bgcolor: 'white',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: isDragging ? 'none' : 'right 0.3s ease-out, bottom 0.3s ease-out',
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      {children}
    </Box>
  );
};

export default DraggableChatWrapper;
